import { mapGetters } from 'vuex';

import { ioGetCustomDocument } from '@/febos/servicios/api/customDocuments.api';
import { evaluateConditions } from '@/febos/global/customDocuments/components/dynamic-file-selector/utils';
import { FORM_TYPE } from '@/febos/global/customDocuments/vistas/empresas/61980520-8/constants';

export default {
  data() {
    const { documentId } = this.$route.query;
    return {
      documentId,
      loadingDraft: false,
      draftKey: 0,
    };
  },
  computed: {
    ...mapGetters('DocManagement', [
      'getRepositories',
    ]),
    filteredAttachments() {
      if (!this.formData.attachments || !this.formData.attachments.length) {
        return [];
      }

      // Obtener los valores de los selectores activos
      const activeSelectorValues = this.schAttachmentsSelectors
        .list
        .filter((s) => evaluateConditions(this.formData, s.conditions))
        .map((s) => s.value);

      // Filtrar archivos cuyo origen NO esté en los selectores activos
      return this.formData.attachments.filter(
        (file) => !activeSelectorValues.includes(file.origen),
      );
    },
  },
  methods: {
    /**
     * Actualiza la ruta para reflejar que estamos editando un borrador existente.
     * Se usa después de crear/guardar un borrador para mantenernos en el formulario.
     */
    async updateRouteAsDraft(documentId) {
      const currentQuery = this.$route.query || {};
      const newQuery = {
        ...currentQuery,
        from: 'draft',
        documentId,
      };
      // Evitar NavigationDuplicated si la query ya es la misma
      const isSameQuery = currentQuery.from === 'draft'
        && currentQuery.documentId === documentId;
      if (!isSameQuery) {
        await this.$router.replace({ query: newQuery });
      }
    },
    async notifyCreation(name) {
      this.$vs.dialog({
        color: 'success',
        title: '¡Genial!',
        text: `Se ha generado el documento ${name}`,
        acceptText: 'Ok'
      });
    },
    cleanAmountValues(obj) {
      if (!obj || typeof obj !== 'object') return;

      const amountPattern = /amount|monto|precio|total|presupuesto|budget/i;

      const clean = (target) => {
        if (Array.isArray(target)) {
          target.forEach((item) => clean(item));
          return;
        }
        if (target && typeof target === 'object') {
          Object.keys(target).forEach((key) => {
            const value = target[key];
            if (typeof value === 'string' && amountPattern.test(key)) {
              // Solo limpiar si tiene símbolo $ o formato de miles (ej: 1.000)
              if (/\$/.test(value) || /^\d{1,3}(\.\d{3})+$/.test(value)) {
                const cleaned = value.replace(/[^\d]/g, '');
                // eslint-disable-next-line no-param-reassign
                target[key] = cleaned ? Number(cleaned) : 0;
              }
            } else if (typeof value === 'object' && value !== null) {
              clean(value);
            }
          });
        }
      };

      clean(obj);
    },
  },
  async mounted() {
    const { from } = this.$route.query;
    const { type } = this.$route.params;
    const { documentId } = this.$route.query;
    this.loadingDraft = true;
    if (from && from === 'draft') {
      this.$vs.notify({
        title: 'Hola!',
        text: 'Estamos buscando la información del documento, esto puede durar unos segundos.',
        color: 'primary',
        time: 10000,
        position: 'top-center'
      });
      const { data } = await ioGetCustomDocument(type, documentId, {});
      this.cleanAmountValues(data.document);
      // Filtrar attachments incompletos y deduplicar por id
      const seenIds = new Set();
      const dedupedAttachments = (data.document.attachments || []).filter((file) => {
        if (!file.id || !file.name) return false;
        if (seenIds.has(file.id)) return false;
        seenIds.add(file.id);
        return true;
      });
      data.document.attachments = dedupedAttachments;
      this.formData = {
        ...data.document,
        status: '1' // si se vuelve a guardar como borrador lo pisa,
        // pero por defecyo debe quedar como "1" para los form que no lo manejan

      };
      this.dynamicSelectorFiles = data.document.attachments
        .filter((file) => file.origen !== 'adjunto');
      this.dfsFiles = data.document.attachments
        .filter((file) => file.origen !== 'adjunto');
      this.attachments = data.document.attachments;
      if (type === 'FCA') {
        this.attachments = data.document.fisabsFilesSelected;
        this.selectedDocument = data.document.attachments
          .find((file) => file.name.includes('FISABS')) || {};
      }
      if (type === 'GRC') {
        this.attachments = data.document.fisabsFilesSelected;
        this.selectedDocument = this.attachments
          .find((file) => file.name.includes('FISABS')) || {};
      }
      if (type === 'SCH') {
        this.experienceFiles = data.document.attachments.filter((file) => file.origen === 'experiencia');
        this.expertiseFiles = data.document.attachments.filter((file) => file.origen === 'expertise');
        // Excluir archivos de experiencia/expertise/adjunto del DFS para evitar duplicados
        const schExcludedOrigins = ['experiencia', 'expertise', 'adjunto'];
        this.dynamicSelectorFiles = data.document.attachments
          .filter((file) => !schExcludedOrigins.includes(file.origen));
      }
      if (type === 'ACT') {
        this.attachments = data.document.cpshFilesSelected;
        this.isDocenteForm = data.document.formType === FORM_TYPE.DOCENTE;
        this.getSelectedFileData(data.document.cpshFilesSelected[0]);
      }
      if (type === 'FISABS') {
        // Excluir el FISABS vinculado ("anterior asociado") del DFS
        const linkedFisabsId = data.document.fisabsSelected
          || (data.document.createdFisabsSelected && data.document.createdFisabsSelected.id)
          || '';
        if (linkedFisabsId) {
          this.dynamicSelectorFiles = this.dynamicSelectorFiles
            .filter((file) => file.id !== linkedFisabsId);
          this.dfsFiles = this.dfsFiles
            .filter((file) => file.id !== linkedFisabsId);
        }
      }
      if (type === 'FISABS') {
        this.formData.items.forEach((item) => {
          if (item.serviceDeliveryPeriodicity && item.serviceDeliveryPeriodicity.length > 0) {
            // Normalización para que lo tome el input de periodicidad,
            // debe ir las fechas en un array sin "hasta" y con el formato correcto
            // Ejemplo: serviceDeliveryPeriodicity: [
            //   "18-02-2026",
            //   "20-02-2026 hasta 22-02-2026"
            // ]
            // debe quedar como
            // serviceDeliveryPeriodicity: [
            //   "2026-02-18",
            //   "2026-02-20",
            //   "2026-02-22"
            // ]
            const normalizedDates = [];
            const normalize = (f) => {
              const partes = f.trim().split('-');
              if (partes[0].length === 4) {
                // yyyy-mm-dd
                return `${partes[0]}-${partes[1].padStart(2, '0')}-${partes[2].padStart(2, '0')}`;
              }
              // dd-mm-yyyy
              return `${partes[2]}-${partes[1].padStart(2, '0')}-${partes[0].padStart(2, '0')}`;
            };
            item.serviceDeliveryPeriodicity.forEach((date) => {
              if (date.includes('hasta')) {
                // Separar el rango
                const [start, end] = date.split('hasta');
                // Normalizar ambas fechas
                normalizedDates.push(normalize(start));
                normalizedDates.push(normalize(end));
              } else {
                normalizedDates.push(normalize(date));
              }
            });
            this.$set(item, 'serviceDeliveryPeriodicity', normalizedDates);
          }
        });
      }

      if (data.document.deliveryDate) {
        this.$set(this.formData, 'deliveryDate', data.document.deliveryDate);
      }
      if (data.document.receptionDate) {
        this.$set(this.formData, 'receptionDate', data.document.receptionDate);
      }

      this.loadingDraft = false;
      this.draftKey += 1;
    } else {
      this.loadingDraft = false;
    }
  },
};
